/**
 * Pure helpers for upserting streaming assistant messages during a multi-interrupt
 * BA turn (KOA-17797). Sequential `ui_common_ask_user_question` rounds used to
 * reuse one streaming messageId and leave prior questionnaire progress +
 * interruptData on the final `## Spec:` bubble — hiding the commitment summary
 * and opening the next round on the wrong question index.
 */

import type { InterruptData, McpChatMessage, QuestionnaireProgress } from "./mcp-chat-types";

export function createEmptyQuestionnaireProgress(): QuestionnaireProgress {
  return {
    currentQuestionIndex: 0,
    selectedValues: {},
    customValues: {}
  };
}

/**
 * True when a new pending question interrupt must open a fresh assistant bubble
 * (and fresh progress) instead of patching the previous questionnaire message.
 *
 * Any prior question bubble stays frozen so users can still see previous answers.
 * Only re-delivery of the *same* toolCallId updates in place.
 */
export function shouldForkStreamingMessageForQuestionInterrupt(
  existing: McpChatMessage | undefined,
  interruptData: InterruptData | undefined
): boolean {
  if (!existing) return false;
  if (!interruptData || interruptData.type !== "question") return false;
  if (interruptData.status !== "pending") return false;
  const prev = existing.interruptData;
  if (!prev || prev.type !== "question") return false;
  // Same interrupt re-delivered (reconnect / duplicate event) → update in place.
  if (
    prev.toolCallId &&
    interruptData.toolCallId &&
    prev.toolCallId === interruptData.toolCallId &&
    prev.status === "pending"
  ) {
    return false;
  }
  // New BA round (or missing toolCallIds) → keep prior answers visible, open a new bubble.
  return true;
}

/**
 * Final BA Spec / model prose must not overwrite a questionnaire bubble — otherwise
 * the last answered round vanishes when Build/Revise appears.
 */
export function shouldForkStreamingMessageForNonInterruptContent(
  existing: McpChatMessage | undefined,
  interruptData: InterruptData | undefined
): boolean {
  if (interruptData !== undefined) return false;
  if (!existing?.interruptData) return false;
  return existing.interruptData.type === "question";
}

export interface StreamingAssistantUpsertInput {
  readonly content: string;
  readonly interruptData?: InterruptData;
  readonly isStreaming?: boolean;
  readonly explorationContent?: string;
  readonly streamingAgent?: string;
}

/**
 * Merge a streaming buffer tick into an existing assistant message.
 * - New interrupt: attach interruptData; keep prior prose if any.
 * - Model/final content without interrupt: clear interruptData so ## Spec:
 *   markdown is visible. Prefer forking Spec onto a new bubble when the
 *   current slot is still a questionnaire (see shouldFork…NonInterruptContent).
 */
export function mergeStreamingAssistantMessage(
  existing: McpChatMessage,
  input: StreamingAssistantUpsertInput
): McpChatMessage {
  const interruptData: InterruptData | undefined = input.interruptData;
  const keepContent: boolean = Boolean(interruptData && existing.content);
  const next: McpChatMessage = {
    ...existing,
    content: keepContent ? existing.content : input.content,
    isStreaming: !!input.isStreaming
  };
  if (interruptData) {
    next.interruptData = interruptData;
  } else {
    // Clear prior gate UI only when intentionally replacing non-questionnaire content.
    delete next.interruptData;
  }
  if (input.explorationContent) {
    next.explorationContent = input.explorationContent;
  }
  if (input.streamingAgent && !existing.streamingAgent) {
    next.streamingAgent = input.streamingAgent;
  }
  return next;
}

/**
 * When a pending question interrupt is applied, progress for that messageId
 * must start at index 0 with empty selections — never inherit the previous
 * round's currentQuestionIndex / selectedValues.
 */
export function questionnaireProgressForPendingQuestion(
  interruptData: InterruptData | undefined
): QuestionnaireProgress | undefined {
  if (!interruptData || interruptData.type !== "question") return undefined;
  if (interruptData.status !== "pending") return undefined;
  return createEmptyQuestionnaireProgress();
}
